import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { StatusBadge } from '../components/StatusBadge';
import { statsAPI, loadAPI, teamAPI } from '../api';
import { TeamCustomerFilters, DASHBOARD_FILTERS_KEY, EMPTY_FILTERS, readFilters } from '../components/TeamCustomerFilters';
import { TrailerDropOff } from '../components/dashboard/TrailerDropOff';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

// Sortable wrapper for a Dashboard widget. Drag handle appears on hover.
const SortableSection = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute -left-1 top-3 z-20 p-1 rounded-full bg-[var(--tms-card)] border border-[var(--tms-border)] text-[var(--tms-text-muted)] cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity hover:text-[var(--tms-primary)] hover:border-[var(--tms-primary)] shadow-sm"
        title="Drag to reorder"
        data-testid={`dashboard-widget-handle-${id}`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {children}
    </div>
  );
};

const DEFAULT_WIDGET_ORDER = ['stats', 'alerts', 'today_schedule', 'customer_margins', 'trend', 'recent_quick', 'commission'];
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { Package, Truck, AlertCircle, AlertTriangle, Calendar, Clock, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TodaySchedule } from '../components/dashboard/TodaySchedule';
import { ActionAlerts } from '../components/dashboard/ActionAlerts';
import { TrendChart } from '../components/dashboard/TrendChart';

// Calculate date ranges for uncovered loads filter
const getUncoveredDateRange = (filter) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let startDate, endDate, label;
  
  if (filter === 'today') {
    startDate = today;
    endDate = today;
    label = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } else if (filter === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    startDate = tomorrow;
    endDate = tomorrow;
    label = tomorrow.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } else if (filter === 'week') {
    // Get Sunday of current week
    const dayOfWeek = today.getDay();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek);
    // Get Saturday
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    startDate = sunday;
    endDate = saturday;
    label = `${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${saturday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    label
  };
};

const Dashboard = () => {
  const { canAccessCommission, isCustomer, isCarrier } = useAuth();
  const isRestrictedUser = isCustomer() || isCarrier();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissionStats, setCommissionStats] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [marginWeekOffset, setMarginWeekOffset] = useState(0);
  const [periodNumber, setPeriodNumber] = useState(null);
  const [uncoveredFilter, setUncoveredFilter] = useState('today');
  const [uncoveredLoads, setUncoveredLoads] = useState({ count: 0, dateRange: '' });
  const [notReleasedLoads, setNotReleasedLoads] = useState([]);
  const [allLoads, setAllLoads] = useState([]);
  const [commissionExpanded, setCommissionExpanded] = useState(false);

  // Widget order — persisted in localStorage so the user's layout survives reloads
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('tms_dashboard_widget_order_v2') || 'null');
      if (Array.isArray(saved) && saved.length === DEFAULT_WIDGET_ORDER.length) return saved;
    } catch (e) { console.warn('Failed to parse saved widget order:', e?.message); }
    return DEFAULT_WIDGET_ORDER;
  });

  useEffect(() => {
    localStorage.setItem('tms_dashboard_widget_order_v2', JSON.stringify(widgetOrder));
  }, [widgetOrder]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleWidgetDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWidgetOrder((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };
  // Team / customer filters — saved so they carry over to the Loads page
  const [teamFilters, setTeamFilters] = useState(() => readFilters(localStorage, DASHBOARD_FILTERS_KEY));
  const [directory, setDirectory] = useState(null);
  const filterOperatorId = teamFilters.operatorId;
  const filterCustomerId = teamFilters.customerId;
  const updateTeamFilters = (next) => {
    setTeamFilters(next);
    localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify(next));
  };
  const navigate = useNavigate();
  
  const showCommission = canAccessCommission();

  const fetchData = useCallback(async () => {
    try {
      const filters = {};
      if (filterOperatorId) filters.operator_id = filterOperatorId;
      if (filterCustomerId) filters.customer_id = filterCustomerId;
      if (marginWeekOffset) filters.margin_week_offset = marginWeekOffset;

      const [statsRes, loadsRes] = await Promise.all([
        statsAPI.getDashboard(filters),
        loadAPI.getAll([], { team_user_id: filterOperatorId, customer_id: filterCustomerId })
      ]);
      setStats(statsRes.data);
      setAllLoads(loadsRes.data);
      
      // Filter loads with "Delivered" status (not yet released)
      const deliveredLoads = loadsRes.data.filter(load => load.status === 'Delivered');
      setNotReleasedLoads(deliveredLoads);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filterOperatorId, filterCustomerId, marginWeekOffset]);

  const fetchUncoveredLoads = useCallback(async () => {
    try {
      const { startDate, endDate, label } = getUncoveredDateRange(uncoveredFilter);
      const res = await statsAPI.getUncoveredLoads(startDate, endDate);
      setUncoveredLoads({ count: res.data.count || 0, dateRange: label });
    } catch (error) {
      console.error('Failed to fetch uncovered loads:', error);
      // Fallback to stats value
      const { label } = getUncoveredDateRange(uncoveredFilter);
      setUncoveredLoads({ count: stats?.uncovered_loads || 0, dateRange: label });
    }
  }, [uncoveredFilter, stats?.uncovered_loads]);

  const fetchCommission = useCallback(async () => {
    try {
      const res = await statsAPI.getCommission(weekOffset, periodNumber);
      setCommissionStats(res.data);
    } catch (error) {
      console.error('Failed to load commission data', error);
      toast.error('Commission data could not be loaded');
    }
  }, [weekOffset, periodNumber]);

  useEffect(() => {
    if (!isRestrictedUser) {
      teamAPI.directory().then(r => setDirectory(r.data)).catch(() => {});
    }
  }, [isRestrictedUser]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchUncoveredLoads(); }, [fetchUncoveredLoads]);
  useEffect(() => { if (showCommission) fetchCommission(); }, [showCommission, fetchCommission]);

  const formatDateRange = (start, end) => {
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[var(--tms-primary)] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-[var(--tms-text-secondary)]">Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" data-testid="dashboard-page">
        <div className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Dashboard</h1>
            <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Overview of your logistics operations</p>
          </div>
          
          {!isRestrictedUser && directory && (
            <TeamCustomerFilters filters={teamFilters} onChange={updateTeamFilters} directory={directory} testPrefix="dash-filter" />
          )}
        </div>

        {/* Sortable widgets - drag the grip handle on hover to reorder */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleWidgetDragEnd}>
          <SortableContext items={widgetOrder} strategy={verticalListSortingStrategy}>
            {widgetOrder.map((widgetId) => {
              switch (widgetId) {
                case 'stats':
                  return (
                    <SortableSection key="stats" id="stats">
        {/* Top Stats Row - KPI Cards (unified NobleUI layout) */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {/* This Week Loads */}
          <div className="stats-card kpi-card" onClick={() => navigate('/loads?filter=this_week&tab=load')} data-testid="stat-this-week-loads">
            <div className="kpi-head">
              <h6 className="kpi-title">Loads This Week</h6>
              <Calendar className="kpi-icon" style={{ color: 'var(--tms-primary)' }} />
            </div>
            <p className="kpi-value" style={{ color: 'var(--tms-primary)' }}>{stats?.this_week_loads || 0}</p>
            <div className="kpi-meta">Picking up {stats?.week_start} - {stats?.week_end}</div>
          </div>

          {/* Uncovered Loads - With Filter */}
          <div className="stats-card kpi-card" data-testid="stat-uncovered-loads">
            <div className="kpi-head">
              <h6 className="kpi-title">Uncovered</h6>
              <AlertTriangle className="kpi-icon" style={{ color: 'var(--tms-error)' }} />
            </div>
            <p
              className="kpi-value cursor-pointer hover:underline"
              style={{ color: 'var(--tms-error)' }}
              onClick={() => navigate('/loads?filter=uncovered&tab=carrier')}
            >
              {uncoveredLoads.count}
            </p>
            <div className="kpi-meta">
              <button
                onClick={() => {
                  const filters = ['today', 'tomorrow', 'week'];
                  const currentIdx = filters.indexOf(uncoveredFilter);
                  if (currentIdx > 0) setUncoveredFilter(filters[currentIdx - 1]);
                }}
                disabled={uncoveredFilter === 'today'}
                className="kpi-nav-btn"
                aria-label="Previous period"
                data-testid="uncovered-prev-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="font-semibold text-[var(--tms-error)]">
                {uncoveredFilter === 'today' ? 'Today' : uncoveredFilter === 'tomorrow' ? 'Tomorrow' : 'Week'}
              </span>
              <button
                onClick={() => {
                  const filters = ['today', 'tomorrow', 'week'];
                  const currentIdx = filters.indexOf(uncoveredFilter);
                  if (currentIdx < filters.length - 1) setUncoveredFilter(filters[currentIdx + 1]);
                }}
                disabled={uncoveredFilter === 'week'}
                className="kpi-nav-btn"
                aria-label="Next period"
                data-testid="uncovered-next-btn"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <span className="truncate">{uncoveredLoads.dateRange}</span>
            </div>
          </div>

          {/* Active Loads - Success state */}
          <div className="stats-card kpi-card" onClick={() => navigate('/loads?filter=active&tab=load')} data-testid="stat-active-loads">
            <div className="kpi-head">
              <h6 className="kpi-title">Active Loads</h6>
              <Truck className="kpi-icon" style={{ color: 'var(--tms-success)' }} />
            </div>
            <p className="kpi-value" style={{ color: 'var(--tms-success)' }}>{stats?.active_loads || 0}</p>
            <div className="kpi-meta">At the Shipper + In Transit</div>
          </div>

          {/* Unscheduled Loads - Warning state */}
          <div className="stats-card kpi-card" onClick={() => navigate('/loads?filter=unscheduled&tab=sched')} data-testid="stat-unscheduled-loads">
            <div className="kpi-head">
              <h6 className="kpi-title">Unschedule</h6>
              <Clock className="kpi-icon" style={{ color: 'var(--tms-warning)' }} />
            </div>
            <p className="kpi-value" style={{ color: 'var(--tms-warning)' }}>{stats?.unscheduled_loads || 0}</p>
            <div className="kpi-meta">Stops needing scheduling</div>
          </div>

          {/* Not Released Loads - Delivered but pending release */}
          <div className="stats-card kpi-card" onClick={() => navigate('/loads?filter=not_released')} data-testid="stat-not-released-loads">
            <div className="kpi-head">
              <h6 className="kpi-title">Not Released</h6>
              <Package className="kpi-icon" style={{ color: 'var(--tms-purple)' }} />
            </div>
            <p className="kpi-value" style={{ color: 'var(--tms-purple)' }}>{notReleasedLoads.length}</p>
            <div className="kpi-meta">Delivered, pending release</div>
          </div>
        </div>

                    </SortableSection>
                  );
                case 'alerts':
                  return (
                    <SortableSection key="alerts" id="alerts">
                      <ActionAlerts loads={allLoads} />
                    </SortableSection>
                  );
                case 'today_schedule':
                  return (
                    <SortableSection key="today_schedule" id="today_schedule">
                      <TodaySchedule loads={allLoads} />
                    </SortableSection>
                  );
                case 'trend':
                  return (
                    <SortableSection key="trend" id="trend">
                      <TrendChart loads={allLoads} />
                    </SortableSection>
                  );
                case 'customer_margins':
                  return (
                    <SortableSection key="customer_margins" id="customer_margins">
        {/* Customer This Week Margin Table */}
        <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6 lg:mb-8">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-[var(--tms-text)]">Customer Margins</h3>
              <p className="text-[10px] sm:text-xs lg:text-sm text-[var(--tms-text-muted)]" data-testid="margin-week-range">
                {stats?.margin_week_start} - {stats?.margin_week_end}{marginWeekOffset === 0 ? ' (this week)' : marginWeekOffset === 1 ? ' (last week)' : ` (${marginWeekOffset} weeks ago)`}
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button onClick={() => setMarginWeekOffset(o => o + 1)} className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--tms-border)] hover:bg-[var(--tms-hover)]" title="Previous week" data-testid="margin-week-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setMarginWeekOffset(o => Math.max(0, o - 1))} disabled={marginWeekOffset === 0} className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--tms-border)] hover:bg-[var(--tms-hover)] disabled:opacity-40" title="Next week" data-testid="margin-week-next">
                <ChevronRight className="w-4 h-4" />
              </button>
              {marginWeekOffset > 0 && (
                <button onClick={() => setMarginWeekOffset(0)} className="h-7 px-2 text-xs font-medium rounded-md border border-[var(--tms-border)] hover:bg-[var(--tms-hover)]" data-testid="margin-week-today">This week</button>
              )}
              <Link to="/customers" className="text-[var(--tms-primary)] hover:underline text-xs sm:text-sm font-medium transition-colors ml-1">
                View All
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="w-full min-w-[400px]">
              <thead>
                <tr className="border-b border-[var(--tms-border)]">
                  <th className="text-left py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium text-[var(--tms-text-muted)]">Customer</th>
                  <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium text-[var(--tms-text-muted)]">Revenue</th>
                  <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium text-[var(--tms-text-muted)]" title="Sum of Net USD across the customer's loads">Total Margin</th>
                  <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium text-[var(--tms-text-muted)]">MPL</th>
                  <th className="text-right py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium text-[var(--tms-text-muted)]">%</th>
                </tr>
              </thead>
              <tbody>
                {(!stats?.customer_margins || stats.customer_margins.length === 0) ? (
                  <tr>
                    <td colSpan="5" className="text-center py-6 sm:py-8 text-xs sm:text-sm text-[var(--tms-text-muted)]">
                      No customer data this week.
                    </td>
                  </tr>
                ) : (
                  stats.customer_margins.map((customer) => (
                    <tr 
                      key={customer.customer_id} 
                      className="border-b border-[var(--tms-border)]/50 cursor-pointer hover:bg-[var(--tms-hover)] transition-colors"
                      onClick={() => navigate(`/customers/${customer.customer_id}`)}
                      data-testid={`customer-row-${customer.customer_id}`}
                    >
                      <td className="py-2 sm:py-3 px-3 sm:px-4 font-medium text-xs sm:text-sm text-[var(--tms-text)] truncate max-w-[120px] sm:max-w-none">{customer.name}</td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm text-[var(--tms-text)]">${customer.total_revenue.toLocaleString()}</td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm font-semibold ${(customer.total_margin || 0) >= 0 ? 'text-[var(--tms-success)]' : 'text-[var(--tms-error)]'}`} data-testid={`customer-total-margin-${customer.customer_id}`}>
                        {(customer.total_margin || 0) < 0 ? "-" : ""}${Math.abs(customer.total_margin || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm text-[var(--tms-success)]">${customer.mpl.toLocaleString()}</td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm">
                        <span className={customer.mpl_pct >= 10 ? 'text-[var(--tms-success)]' : customer.mpl_pct >= 5 ? 'text-[var(--tms-warning)]' : 'text-[var(--tms-error)]'}>
                          {customer.mpl_pct}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {stats?.customer_margins?.length > 0 && (() => {
                const rows = stats.customer_margins;
                const rev = rows.reduce((a, c) => a + (c.total_revenue || 0), 0);
                const mar = rows.reduce((a, c) => a + (c.total_margin || 0), 0);
                const usd = (v) => `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <tfoot>
                    <tr className="border-t-2 border-[var(--tms-border)] bg-[var(--tms-hover)]" data-testid="customer-margins-totals">
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-bold text-[var(--tms-text)]">Total <span className="font-normal text-[var(--tms-text-muted)]">({rows.length} customers)</span></td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm font-bold text-[var(--tms-text)]" data-testid="customer-margins-total-revenue">{usd(rev)}</td>
                      <td className={`py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm font-bold ${mar >= 0 ? 'text-[var(--tms-success)]' : 'text-[var(--tms-error)]'}`} data-testid="customer-margins-total-margin">{usd(mar)}</td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm text-[var(--tms-text-muted)]">—</td>
                      <td className="py-2 sm:py-3 px-3 sm:px-4 text-right font-mono text-xs sm:text-sm font-bold text-[var(--tms-text)]">{rev > 0 ? `${((mar / rev) * 100).toFixed(1)}%` : '—'}</td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </div>

                    </SortableSection>
                  );
                case 'commission':
                  return (
                    <SortableSection key="commission" id="commission">
        {/* Commission Section - Only visible with commission permission */}
        {showCommission && commissionStats && (() => {
          const hasVal = (o) => o && ['load_count', 'total_margin', 'total_commission'].some((k) => (o[k] || 0) > 0);
          const isEmpty = !hasVal(commissionStats.weekly) && !hasVal(commissionStats.period);
          if (isEmpty && !commissionExpanded) {
            return (
              <div className="stats-card mb-4 sm:mb-6 lg:mb-8 flex items-center justify-between" style={{ padding: '0.875rem 1.5rem' }} data-testid="commission-collapsed">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[var(--tms-success)]" />
                  <span className="text-sm text-[var(--tms-text-secondary)]">Your Commission — no commission activity this week/period</span>
                </div>
                <button className="text-xs font-semibold text-[var(--tms-primary)] hover:underline" onClick={() => setCommissionExpanded(true)} data-testid="commission-expand">
                  Show details
                </button>
              </div>
            );
          }
          return (
          <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6 lg:mb-8" data-testid="commission-section">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[var(--tms-success)]/10 flex items-center justify-center">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--tms-success)]" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-[var(--tms-text)]">Your Commission</h3>
                <span className="text-xs sm:text-sm text-[var(--tms-text-muted)]">{commissionStats.user_name}</span>
              </div>
              {isEmpty && (
                <button className="text-xs font-semibold text-[var(--tms-text-muted)] hover:text-[var(--tms-text)]" onClick={() => setCommissionExpanded(false)} data-testid="commission-collapse">
                  Hide
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
              {/* Weekly Commission */}
              <div className="border border-[var(--tms-primary)]/30 rounded-xl p-4 bg-[var(--tms-primary)]/5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-[var(--tms-text)]">Weekly</h4>
                    <p className="text-xs text-[var(--tms-text-muted)]">
                      {formatDateRange(commissionStats.weekly?.start_date, commissionStats.weekly?.end_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setWeekOffset(weekOffset + 1)}
                      className="p-1 h-7 w-7 text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)] hover:bg-[var(--tms-hover)]"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {weekOffset > 0 && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setWeekOffset(weekOffset - 1)}
                          className="p-1 h-7 w-7 text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)] hover:bg-[var(--tms-hover)]"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setWeekOffset(0)}
                          className="text-xs h-7 px-2 text-[var(--tms-primary)] hover:bg-[var(--tms-primary)]/10"
                        >
                          Current
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-text)]">{commissionStats.weekly?.load_count || 0}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Loads</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-primary)]">${(commissionStats.weekly?.total_margin || 0).toLocaleString()}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Margin</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-success)]">${(commissionStats.weekly?.total_commission || 0).toLocaleString()}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Commission</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--tms-primary)]/20 flex justify-center gap-6 text-xs text-[var(--tms-text-muted)]">
                  <span>Manager: {commissionStats.weekly?.as_manager || 0}</span>
                  <span>Operator: {commissionStats.weekly?.as_operator || 0}</span>
                </div>
              </div>

              {/* Period Commission */}
              <div className="border border-[var(--tms-purple)]/30 rounded-xl p-4 bg-[var(--tms-purple)]/5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-[var(--tms-text)]">Period {commissionStats.period?.period_number || 1}</h4>
                    <p className="text-xs text-[var(--tms-text-muted)]">
                      {formatDateRange(commissionStats.period?.start_date, commissionStats.period?.end_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPeriodNumber((commissionStats.period?.period_number || 1) - 1)}
                      disabled={commissionStats.period?.period_number <= 1}
                      className="p-1 h-7 w-7 text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)] hover:bg-[var(--tms-hover)]"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPeriodNumber((commissionStats.period?.period_number || 1) + 1)}
                      className="p-1 h-7 w-7 text-[var(--tms-text-secondary)] hover:text-[var(--tms-text)] hover:bg-[var(--tms-hover)]"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    {periodNumber && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setPeriodNumber(null)}
                        className="text-xs h-7 px-2 text-[var(--tms-purple)] hover:bg-[var(--tms-purple)]/10"
                      >
                        Current
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-text)]">{commissionStats.period?.load_count || 0}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Loads</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-purple)]">${(commissionStats.period?.total_margin || 0).toLocaleString()}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Margin</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--tms-success)]">${(commissionStats.period?.total_commission || 0).toLocaleString()}</p>
                    <p className="text-xs text-[var(--tms-text-muted)]">Commission</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--tms-purple)]/20 flex justify-center gap-6 text-xs text-[var(--tms-text-muted)]">
                  <span>Manager: {commissionStats.period?.as_manager || 0}</span>
                  <span>Operator: {commissionStats.period?.as_operator || 0}</span>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

                    </SortableSection>
                  );
                case 'recent_quick':
                  return (
                    <SortableSection key="recent_quick" id="recent_quick">
        {/* Trailer Drop Off & Quick Actions Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Trailer Drop Off - same parameters as Loads tab drop-off kanban */}
          <TrailerDropOff loads={allLoads} />

          {/* Quick Actions */}
          <div>
            <div className="stats-card" style={{ padding: '1rem 1.25rem' }}>
            <h3 className="text-sm font-semibold uppercase text-[var(--tms-text)] mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                className="w-full bg-[var(--tms-primary)] hover:bg-[var(--tms-primary-hover)] text-white py-2.5 px-4 rounded text-left text-sm font-medium transition-colors"
                onClick={async () => {
                  try {
                    const res = await loadAPI.createBlank();
                    navigate(`/loads/${res.data.id}`);
                  } catch (err) {
                    toastApiError(toast, err, 'Load could not be created');
                  }
                }}
                data-testid="quick-create-load"
              >
                + Create New Load
              </button>
              <Link to="/customers/new" className="block">
                <button className="w-full bg-[var(--tms-card)] hover:bg-[var(--tms-hover)] text-[var(--tms-text)] py-2.5 px-4 rounded text-left text-sm font-medium transition-colors border border-[var(--tms-border)]" data-testid="quick-create-customer">
                  + Add Customer
                </button>
              </Link>
              <Link to="/carriers/new" className="block">
                <button className="w-full bg-[var(--tms-card)] hover:bg-[var(--tms-hover)] text-[var(--tms-text)] py-2.5 px-4 rounded text-left text-sm font-medium transition-colors border border-[var(--tms-border)]" data-testid="quick-create-carrier">
                  + Add Carrier
                </button>
              </Link>
            </div>
            
            {/* Summary Stats */}
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-[var(--tms-border)]">
              <h4 className="text-xs sm:text-sm font-semibold text-[var(--tms-text-secondary)] mb-2 sm:mb-3">Overall Stats</h4>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--tms-text-muted)]">Total Revenue</span>
                  <span className="font-mono font-medium text-[var(--tms-text)]">${stats?.total_revenue?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--tms-text-muted)]">Total Margin</span>
                  <span className="font-mono font-medium text-[var(--tms-success)]">${stats?.total_margin?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--tms-text-muted)]">Avg Margin %</span>
                  <span className="font-mono font-medium text-[var(--tms-text)]">{stats?.avg_margin_pct?.toFixed(1) || 0}%</span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
                    </SortableSection>
                  );
                default:
                  return null;
              }
            })}
          </SortableContext>
        </DndContext>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
